import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Plus, Upload, Edit, Trash2, Save, X, Video, Music, Image, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface KaleidoscopeOption {
  id: string;
  title: string;
  description?: string;
  video_path: string;
  audio_path?: string;
  duration: number;
  thumbnail_path?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface KaleidoscopeForm {
  title: string;
  description: string;
  duration: number;
  is_active: boolean;
}

export default function KaleidoscopeManager() {
  const [kaleidoscopes, setKaleidoscopes] = useState<KaleidoscopeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<KaleidoscopeForm>({
    title: '',
    description: '',
    duration: 30,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    video?: number;
    audio?: number;
    thumbnail?: number;
  }>({});
  const { toast } = useToast();

  useEffect(() => {
    loadKaleidoscopes();
  }, []);

  const loadKaleidoscopes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('kaleidoscope_options')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setKaleidoscopes(data || []);
    } catch (error) {
      console.error('Error loading kaleidoscopes:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load kaleidoscope options. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast({
        title: "Validation Error",
        description: "Title is required.",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData(e.target as HTMLFormElement);
      
      // Upload files
      const videoFile = formData.get('video') as File;
      const audioFile = formData.get('audio') as File;
      const thumbnailFile = formData.get('thumbnail') as File;

      if (!videoFile && !editingId) {
        toast({
          title: "Validation Error",
          description: "Video file is required for new kaleidoscope options.",
          variant: "destructive"
        });
        return;
      }

      // Validate file sizes (in MB)
      const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
      const MAX_AUDIO_SIZE = 20 * 1024 * 1024; // 20MB
      const MAX_THUMBNAIL_SIZE = 5 * 1024 * 1024; // 5MB

      if (videoFile && videoFile.size > 0 && videoFile.size > MAX_VIDEO_SIZE) {
        toast({
          title: "File too large",
          description: `Video file must be less than 50MB. Current size: ${(videoFile.size / 1024 / 1024).toFixed(1)}MB`,
          variant: "destructive"
        });
        return;
      }

      if (audioFile && audioFile.size > 0 && audioFile.size > MAX_AUDIO_SIZE) {
        toast({
          title: "File too large",
          description: `Audio file must be less than 20MB. Current size: ${(audioFile.size / 1024 / 1024).toFixed(1)}MB`,
          variant: "destructive"
        });
        return;
      }

      if (thumbnailFile && thumbnailFile.size > 0 && thumbnailFile.size > MAX_THUMBNAIL_SIZE) {
        toast({
          title: "File too large",
          description: `Thumbnail must be less than 5MB. Current size: ${(thumbnailFile.size / 1024 / 1024).toFixed(1)}MB`,
          variant: "destructive"
        });
        return;
      }

      let videoPath = '';
      let audioPath = '';
      let thumbnailPath = '';

      // Upload video if provided
      if (videoFile && videoFile.size > 0) {
        const videoFileName = `${Date.now()}-${videoFile.name}`;
        const { data: videoData, error: videoError } = await supabase.storage
          .from('kaleidoscope-videos')
          .upload(videoFileName, videoFile);

        if (videoError) throw videoError;
        videoPath = videoData.path;
      }

      // Upload audio if provided
      if (audioFile && audioFile.size > 0) {
        const audioFileName = `${Date.now()}-${audioFile.name}`;
        const { data: audioData, error: audioError } = await supabase.storage
          .from('kaleidoscope-audio')
          .upload(audioFileName, audioFile);

        if (audioError) throw audioError;
        audioPath = audioData.path;
      }

      // Upload thumbnail if provided
      if (thumbnailFile && thumbnailFile.size > 0) {
        const thumbnailFileName = `${Date.now()}-${thumbnailFile.name}`;
        const { data: thumbnailData, error: thumbnailError } = await supabase.storage
          .from('kaleidoscope-thumbnails')
          .upload(thumbnailFileName, thumbnailFile);

        if (thumbnailError) throw thumbnailError;
        thumbnailPath = thumbnailData.path;
      }

      // Prepare data for database
      const dbData: any = {
        title: form.title,
        description: form.description || null,
        duration: form.duration,
        is_active: form.is_active
      };

      if (videoPath) dbData.video_path = videoPath;
      if (audioPath) dbData.audio_path = audioPath;
      if (thumbnailPath) dbData.thumbnail_path = thumbnailPath;

      if (editingId) {
        // Update existing
        const { error } = await supabase
          .from('kaleidoscope_options')
          .update(dbData)
          .eq('id', editingId);

        if (error) throw error;

        toast({
          title: "Updated successfully",
          description: "Kaleidoscope option has been updated."
        });
      } else {
        // Create new
        if (!videoPath) {
          throw new Error('Video file is required for new kaleidoscope options');
        }
        
        const { error } = await supabase
          .from('kaleidoscope_options')
          .insert([dbData]);

        if (error) throw error;

        toast({
          title: "Created successfully",
          description: "New kaleidoscope option has been created."
        });
      }

      // Reset form
      setForm({
        title: '',
        description: '',
        duration: 30,
        is_active: true
      });
      setEditingId(null);
      setShowForm(false);
      loadKaleidoscopes();

    } catch (error) {
      console.error('Error saving kaleidoscope:', error);
      toast({
        title: "Error saving",
        description: "Failed to save kaleidoscope option. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const handleEdit = (kaleidoscope: KaleidoscopeOption) => {
    setForm({
      title: kaleidoscope.title,
      description: kaleidoscope.description || '',
      duration: kaleidoscope.duration,
      is_active: kaleidoscope.is_active
    });
    setEditingId(kaleidoscope.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('kaleidoscope_options')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Deleted successfully",
        description: "Kaleidoscope option has been deleted."
      });

      loadKaleidoscopes();
    } catch (error) {
      console.error('Error deleting kaleidoscope:', error);
      toast({
        title: "Error deleting",
        description: "Failed to delete kaleidoscope option. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('kaleidoscope_options')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Status updated",
        description: `Kaleidoscope option has been ${!currentStatus ? 'activated' : 'deactivated'}.`
      });

      loadKaleidoscopes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error updating",
        description: "Failed to update kaleidoscope status. Please try again.",
        variant: "destructive"
      });
    }
  };

  const cancelEdit = () => {
    setForm({
      title: '',
      description: '',
      duration: 30,
      is_active: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Kaleidoscope Manager</h2>
          <p className="text-muted-foreground">Manage hypnotic induction videos and patterns</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-gradient-primary hover:bg-primary text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Kaleidoscope
        </Button>
      </div>

      {showForm && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {editingId ? 'Edit Kaleidoscope Option' : 'Add New Kaleidoscope Option'}
            </h3>
            <Button variant="ghost" size="sm" onClick={cancelEdit}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter kaleidoscope title"
                  required
                />
              </div>

              <div>
                <Label htmlFor="duration">Duration (seconds) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="5"
                  max="300"
                  value={form.duration}
                  onChange={(e) => setForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description (optional)"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="video">Video File {!editingId && '*'}</Label>
                <Input
                  id="video"
                  name="video"
                  type="file"
                  accept="video/*"
                  className="cursor-pointer"
                  required={!editingId}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  MP4, MOV, AVI (max 50MB)
                </p>
              </div>

              <div>
                <Label htmlFor="audio">Audio File (optional)</Label>
                <Input
                  id="audio"
                  name="audio"
                  type="file"
                  accept="audio/*"
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  MP3, WAV, OGG (max 20MB)
                </p>
              </div>

              <div>
                <Label htmlFor="thumbnail">Thumbnail (optional)</Label>
                <Input
                  id="thumbnail"
                  name="thumbnail"
                  type="file"
                  accept="image/*"
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, WEBP (max 5MB)
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Active (visible to users)</Label>
            </div>

            <div className="flex gap-2">
              <Button 
                type="submit" 
                disabled={uploading}
                className="bg-gradient-primary hover:bg-primary text-primary-foreground"
              >
                {uploading ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    {editingId ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {kaleidoscopes.map((kaleidoscope) => (
          <Card key={kaleidoscope.id} className="overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
              <Video className="w-12 h-12 text-primary/50" />
            </div>
            
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-lg">{kaleidoscope.title}</h3>
                <Badge variant={kaleidoscope.is_active ? "default" : "secondary"}>
                  {kaleidoscope.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              
              {kaleidoscope.description && (
                <p className="text-muted-foreground text-sm mb-3">
                  {kaleidoscope.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {kaleidoscope.duration}s
                </div>
                {kaleidoscope.audio_path && (
                  <div className="flex items-center">
                    <Music className="w-4 h-4 mr-1" />
                    Audio
                  </div>
                )}
                {kaleidoscope.thumbnail_path && (
                  <div className="flex items-center">
                    <Image className="w-4 h-4 mr-1" />
                    Thumbnail
                  </div>
                )}
              </div>

              <Separator className="mb-4" />

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(kaleidoscope)}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActive(kaleidoscope.id, kaleidoscope.is_active)}
                >
                  {kaleidoscope.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(kaleidoscope.id, kaleidoscope.title)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {kaleidoscopes.length === 0 && !loading && (
        <Card className="p-8 text-center">
          <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No kaleidoscope options yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first kaleidoscope option to provide users with hypnotic induction choices.
          </p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-gradient-primary hover:bg-primary text-primary-foreground"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Kaleidoscope
          </Button>
        </Card>
      )}
    </div>
  );
}